import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { AppShell } from '@/components/AppShell';
import { DashboardSkeleton } from '@/components/DashboardSkeleton';
import { OnboardingSkeleton } from '@/components/OnboardingSkeleton';
import { DashboardPage } from '@/pages/DashboardPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { GhlInstallPage } from '@/pages/GhlInstallPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { OnboardingGoLivePage } from '@/pages/OnboardingGoLivePage';
import { OnboardingWebsitePage } from '@/pages/OnboardingWebsitePage';
import { OnboardingConnectPage } from '@/pages/OnboardingConnectPage';
import { OnboardingGoogleAdsPickPage } from '@/pages/OnboardingGoogleAdsPickPage';
import { OnboardingGoogleAdsSetupPage } from '@/pages/OnboardingGoogleAdsSetupPage';
import { OnboardingFacebookPickPage } from '@/pages/OnboardingFacebookPickPage';
import { OnboardingFacebookSetupPage } from '@/pages/OnboardingFacebookSetupPage';
import { OnboardingSuccessPage } from '@/pages/OnboardingSuccessPage';
import { OnboardingSkippedPage } from '@/pages/OnboardingSkippedPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { WebsitePage } from '@/pages/WebsitePage';
import { InsightsPage } from '@/pages/InsightsPage';
import { AccountPage } from '@/pages/AccountPage';
import { AuthProvider, useAuth } from '@/lib/useAuth';

function ProtectedRoute({
  children,
  loadingFallback,
}: {
  children: React.ReactNode;
  loadingFallback?: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      loadingFallback ?? (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

function RequireClient({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth();
  if (loading) return <DashboardSkeleton />;
  // Bounce users still in onboarding to the matching screen.
  if (me?.next_step === 'business')
    return <Navigate to="/onboarding" replace />;
  if (me?.next_step === 'website')
    return <Navigate to="/onboarding/website" replace />;
  if (me?.next_step === 'connect')
    return <Navigate to="/onboarding/connect" replace />;
  if (me?.next_step === 'go_live')
    return <Navigate to="/onboarding/go-live" replace />;
  return <>{children}</>;
}

function RequireStep({
  step,
  children,
}: {
  step: 'business' | 'website' | 'connect' | 'go_live';
  children: React.ReactNode;
}) {
  const { me, loading } = useAuth();
  const skeletonStep =
    step === 'business'
      ? 1
      : step === 'website'
        ? 2
        : step === 'connect'
          ? 3
          : 4;
  if (loading) return <OnboardingSkeleton current={skeletonStep} />;
  if (!me) return null;

  // System admins can preview any onboarding page for design/copy QA.
  if (me.is_system_admin) return <>{children}</>;

  // Lock onboarding URLs once user is finished (everything connected OR
  // explicitly skipped → next_step === null). Editing happens in Settings.
  // TEMPORARILY DISABLED FOR TESTING — re-enable before shipping.
  // if (me.next_step === null) return <Navigate to="/" replace />;

  // Steps 2/3/4 require a client. Otherwise route to Step 1 first.
  const hasClient = !!me.memberships?.length;
  if (step !== 'business' && !hasClient) {
    return <Navigate to="/onboarding" replace />;
  }

  // Free navigation across all steps. Step 1 page handles its own revisit
  // logic (loads existing data + PATCH on submit when client exists).
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/signin" element={<LoginPage />} />
      <Route path="/login" element={<Navigate to="/signin" replace />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* Platform-generic path — GHL's white-label rules forbid "ghl"/"highlevel"
          in the registered redirect URL. Internal names stay GhlInstallPage etc. */}
      <Route path="/crm/install" element={<GhlInstallPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={1} />}>
            <AuthProvider>
              <RequireStep step="business">
                <OnboardingPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/website"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={2} />}>
            <AuthProvider>
              <RequireStep step="website">
                <OnboardingWebsitePage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/connect"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={3} />}>
            <AuthProvider>
              <RequireStep step="connect">
                <OnboardingConnectPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/connect/google-pick"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={3} />}>
            <AuthProvider>
              <RequireStep step="connect">
                <OnboardingGoogleAdsPickPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/connect/google-setup"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={3} />}>
            <AuthProvider>
              <RequireStep step="connect">
                <OnboardingGoogleAdsSetupPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/connect/facebook-pick"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={3} />}>
            <AuthProvider>
              <RequireStep step="connect">
                <OnboardingFacebookPickPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/connect/facebook-setup"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={3} />}>
            <AuthProvider>
              <RequireStep step="connect">
                <OnboardingFacebookSetupPage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/go-live"
        element={
          <ProtectedRoute loadingFallback={<OnboardingSkeleton current={4} />}>
            <AuthProvider>
              <RequireStep step="go_live">
                <OnboardingGoLivePage />
              </RequireStep>
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      {/* Success + Skipped — top-level (not under RequireClient) so users
          can land here after clicking Done on /onboarding/go-live without the
          gate bouncing them back into onboarding. Reached by explicit
          navigate() from the go-live page. */}
      <Route
        path="/success"
        element={
          <ProtectedRoute>
            <AuthProvider>
              <OnboardingSuccessPage />
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/skipped"
        element={
          <ProtectedRoute>
            <AuthProvider>
              <OnboardingSkippedPage />
            </AuthProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute loadingFallback={<DashboardSkeleton />}>
            <AuthProvider>
              <RequireClient>
                <AppShell />
              </RequireClient>
            </AuthProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="website" element={<WebsitePage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="account" element={<AccountPage />} />
      </Route>
      {/* Catch-all 404 — HeyMotek-branded with a "Back to dashboard" CTA. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
